import { updateMegaMenuActiveStates } from "./ekMegaMenu";
import { NAV_LINK_SELECTOR } from "./collectionPage/constants";
import { getHeaderSection, getGridSection, qs } from "./collectionPage/dom";
import { refreshCollectionLoadMore } from "./collectionPage/loadMore";
import { shouldPreserveSubcollectionNav } from "./collectionPage/patchHeader";
import { applyCollectionUpdate, initCollectionPageRouter } from "./collectionPage/router";
import { collectionHandleFromPath } from "./collectionPage/urls";
import type { CollectionHistoryState } from "./collectionPage/history";

const REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)");

let scrollAbort: AbortController | null = null;
let navEventsBound = false;

const getLiveRegion = () => qs<HTMLElement>("#ek-collection-live-region");

const getAnnounceMessage = (state: "loading" | "loaded") => {
  const region = getLiveRegion();
  if (!region) return "";
  return state === "loading" ? region.dataset.loadingMessage ?? "" : region.dataset.loadedMessage ?? "";
};

const announce = (message: string) => {
  if (!message) return;
  const region = getLiveRegion();
  if (region) region.textContent = message;
};

const setSubcollectionNavActive = (activeHandle: string) => {
  const menu = qs<HTMLElement>("#subcollections-menu");
  if (!menu) return;

  for (const link of Array.from(menu.querySelectorAll<HTMLAnchorElement>(NAV_LINK_SELECTOR))) {
    const linkHandle = collectionHandleFromPath(new URL(link.href, window.location.origin).pathname);
    const isActive = linkHandle === activeHandle;
    link.classList.toggle("subcollection-active", isActive);
    link.classList.toggle("subcollection-inactive", !isActive);
    if (isActive) link.setAttribute("aria-current", "page");
    else link.removeAttribute("aria-current");
  }
};

const updateScrollIndicators = () => {
  const scrollContainer = qs<HTMLElement>("#subcollection-nav");
  const leftContainer = qs<HTMLElement>("#left-scroll-container");
  const rightContainer = qs<HTMLElement>("#right-scroll-container");
  if (!scrollContainer || !leftContainer || !rightContainer) return;

  const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
  const maxScrollLeft = scrollWidth - clientWidth;
  leftContainer.classList.toggle("hidden", scrollLeft <= 10);
  rightContainer.classList.toggle("hidden", scrollLeft >= maxScrollLeft - 10);
};

export const initializeScrollIndicators = (scope: ParentNode = document) => {
  const scrollContainer = qs<HTMLElement>("#subcollection-nav", scope);
  const leftBtn = qs<HTMLButtonElement>("#scroll-left-btn", scope);
  const rightBtn = qs<HTMLButtonElement>("#scroll-right-btn", scope);
  if (!scrollContainer || !leftBtn || !rightBtn) return;

  scrollAbort?.abort();
  scrollAbort = new AbortController();
  const { signal } = scrollAbort;

  leftBtn.addEventListener(
    "click",
    () => {
      scrollContainer.scrollBy({ left: -scrollContainer.clientWidth * 0.8, behavior: REDUCED_MOTION.matches ? "auto" : "smooth" });
    },
    { signal },
  );

  rightBtn.addEventListener(
    "click",
    () => {
      scrollContainer.scrollBy({ left: scrollContainer.clientWidth * 0.8, behavior: REDUCED_MOTION.matches ? "auto" : "smooth" });
    },
    { signal },
  );

  scrollContainer.addEventListener("scroll", updateScrollIndicators, { signal });
  requestAnimationFrame(updateScrollIndicators);
};

export const scrollToActiveSubcollection = (scope: ParentNode = document) => {
  const scrollContainer = qs<HTMLElement>("#subcollection-nav", scope);
  if (!scrollContainer) return;
  const activeLink = scrollContainer.querySelector<HTMLElement>("[aria-current='page']");
  if (!activeLink) return;

  const target = activeLink.offsetLeft - scrollContainer.clientWidth / 2 + activeLink.offsetWidth / 2;
  const targetScrollLeft = Math.max(0, Math.min(target, scrollContainer.scrollWidth - scrollContainer.clientWidth));
  scrollContainer.scrollTo({ left: targetScrollLeft, behavior: REDUCED_MOTION.matches ? "auto" : "smooth" });
  setTimeout(updateScrollIndicators, 300);
};

const focusCollectionHeading = (headerSection: HTMLElement) => {
  const heading = headerSection.querySelector<HTMLElement>("h1[tabindex='-1']") || headerSection.querySelector<HTMLElement>("h1");
  heading?.focus({ preventScroll: true });
};

export const handleNavAfterCollectionUpdate = (ctx: {
  preserveSubcollectionNav?: boolean;
  headerSection?: HTMLElement | null;
  activeHandle?: string;
  navParent: string | null;
}) => {
  if (ctx.preserveSubcollectionNav && ctx.navParent && ctx.activeHandle) {
    setSubcollectionNavActive(ctx.activeHandle);
  }

  if (!ctx.preserveSubcollectionNav) {
    initializeScrollIndicators(document);
  }

  scrollToActiveSubcollection(document);
  updateMegaMenuActiveStates();

  if (ctx.headerSection) {
    focusCollectionHeading(ctx.headerSection);
  }

  announce(getAnnounceMessage("loaded"));
};

const loadCollectionFromUrl = async (rawUrl: string, navParent: string | null, pushHistory = true) => {
  const headerSection = getHeaderSection();
  const gridSection = getGridSection();
  if (!headerSection || !gridSection) return;

  announce(getAnnounceMessage("loading"));

  const pathUrl = new URL(rawUrl, window.location.origin);
  const preserveNav = shouldPreserveSubcollectionNav(headerSection, navParent);

  await applyCollectionUpdate({
    pathname: pathUrl.pathname,
    search: pathUrl.search,
    navParent,
    mode: "nav",
    pushHistory,
    preserveSubcollectionNav: preserveNav,
    showSkeleton: true,
  });
};

const handlePopState = (state: CollectionHistoryState) => {
  loadCollectionFromUrl(state.url, state.navParent ?? null, false);
};

const handleNavClick = (event: Event) => {
  if (!getHeaderSection()) return;

  const mouseEvent = event as MouseEvent;
  if (mouseEvent.button !== 0) return;

  const target = (event.target as Element | null)?.closest<HTMLAnchorElement>(NAV_LINK_SELECTOR);
  if (!target) return;
  if (mouseEvent.metaKey || mouseEvent.ctrlKey || mouseEvent.shiftKey || mouseEvent.altKey) return;

  event.preventDefault();
  event.stopPropagation();

  const navParent = target.dataset.navParent || null;
  loadCollectionFromUrl(target.href, navParent);
};

const bindNavEventsOnce = () => {
  if (navEventsBound) return;
  navEventsBound = true;
  document.addEventListener("click", handleNavClick, true);
};

export const initCollectionNav = (scope: ParentNode = document) => {
  if (!getHeaderSection(scope)) return;

  bindNavEventsOnce();
  initializeScrollIndicators(scope);
  scrollToActiveSubcollection(scope);
  refreshCollectionLoadMore();
  window.ProductCardSwatches?.initAll(scope);
};

export const initCollectionNavRouter = () => {
  initCollectionPageRouter(handlePopState);
};

export { loadCollectionFromUrl };
